import React, { useEffect, useMemo } from 'react'
import { ThreeDots } from '../../common/components/loaders/ThreeDots/ThreeDots'
import { LegacyEditorVersion, setLegacyEditorVersion } from '../../util/editorPreference'
import styles from './PlayerAccountView.module.css'
import { useGameData } from '../../contexts/PlayerAccountGamesContext'
import { LoaderWithOverlay } from '../../composites/GamesOverview/components/LoaderWithOverlay'
import { EmptyContent, EmptyContentProps } from '../../composites/GamesOverview/components/EmptyContent'
import { PlayerAccountGamesGrid } from './components/PlayerAccountGamesGrid'
import { useTranslation } from 'react-i18next'
import { TPlayerGameCard } from '../../types/commonTypes'

type PlayerAccountGamesOverviewProps = {
  emptyContent?: EmptyContentProps
}

// Helper: sort by joined desc (newest first), guarding invalid dates
const byJoinedDesc = (a: TPlayerGameCard, b: TPlayerGameCard) => {
  const aTime = a.joined instanceof Date && !isNaN(a.joined.getTime()) ? a.joined.getTime() : 0
  const bTime = b.joined instanceof Date && !isNaN(b.joined.getTime()) ? b.joined.getTime() : 0
  return bTime - aTime
}

export const PlayerAccountGamesOverview: React.FC<PlayerAccountGamesOverviewProps> = ({ emptyContent }) => {
  const { loading, pageData } = useGameData()
  const { t } = useTranslation()

  useEffect(() => {
    setLegacyEditorVersion(LegacyEditorVersion.LEGACY_FRAME)
  }, [])

  const withProgressGames = useMemo(() => {
    if (pageData) {
      // In progress: has started (>=1) but not completed
      const withProgress = pageData.filter((data) => {
        const done = data.gamerExerciseCount ?? 0
        const isOpen = data.open
        return done >= 1 && done < data.exerciseCount && isOpen
      })

      withProgress.sort(byJoinedDesc)
      return withProgress
    }
  }, [pageData])

  const noProgressGames = useMemo(() => {
    if (pageData) {
      // Ready to play: not completed and not started yet (<1 done)
      const noProgress = pageData.filter((data) => {
        const done = data.gamerExerciseCount ?? 0
        const isOpen = data.open
        return done < 1 && done !== data.exerciseCount && isOpen
      })

      noProgress.sort(byJoinedDesc)
      return noProgress
    }
  }, [pageData])

  const closedGames = useMemo(() => {
    if (pageData) {
      // Closed: visible but not open yet (and not finished)
      const closed = pageData.filter((data) => {
        const isOpen = data.open
        const isFinished = data.gamerExerciseCount === data.exerciseCount
        return !isOpen && !isFinished
      })

      closed.sort(byJoinedDesc)
      return closed
    }
  }, [pageData])

  const completed = useMemo(() => {
    if (pageData) {
      const alreadyCompleted = pageData.filter((data) => data.gamerExerciseCount === data.exerciseCount)

      const sorted = [...alreadyCompleted].sort((a, b) => {
        const aTime = a.joined instanceof Date && !isNaN(a.joined.getTime()) ? a.joined.getTime() : 0
        const bTime = b.joined instanceof Date && !isNaN(b.joined.getTime()) ? b.joined.getTime() : 0
        // Newest (most recently added) first
        return bTime - aTime
      })
      return sorted
    }
  }, [pageData])

  return (
    <>
      <div className={styles.gamesAndPaginationContainer}>
        {loading && (
          <LoaderWithOverlay>
            <ThreeDots />
          </LoaderWithOverlay>
        )}

        {withProgressGames != null && withProgressGames?.length > 0 && (
          <div className={styles.gamesGridContainer}>
            <h2>{t('dashboard.continue_playing', 'Continue playing')}</h2>
            <PlayerAccountGamesGrid games={withProgressGames} loading={loading} />
          </div>
        )}

        {noProgressGames != null && noProgressGames?.length > 0 && (
          <div className={styles.gamesGridContainer}>
            <h2>{t('dashboard.ready_to_play', 'Ready to play')}</h2>
            <PlayerAccountGamesGrid games={noProgressGames} loading={loading} />
          </div>
        )}
        {closedGames != null && closedGames?.length > 0 && (
          <div className={styles.gamesGridContainer}>
            <h2>{t('dashboard.closed', 'Closed games')}</h2>
            <p className={styles.listInstructionText}>
              {t('login.login_failed_game_not_open.content', 'Please wait for the instructor to start the game.')}
            </p>
            <PlayerAccountGamesGrid games={closedGames} loading={loading} />
          </div>
        )}
        {completed != null && completed?.length > 0 && (
          <div className={styles.gamesGridContainer}>
            <h2>{t('dashboard.completed', 'Completed games')}</h2>
            <PlayerAccountGamesGrid games={completed} loading={loading} />
          </div>
        )}
        {emptyContent != null && (pageData == null || pageData?.length === 0) && (
          <EmptyContent title={emptyContent.title} subtitle={emptyContent.subtitle} />
        )}
      </div>
    </>
  )
}
